// src/components/pages/myAGT/MyAGTProfile.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme,
  styled,
  Fade,
  Collapse,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Slide,
  InputAdornment,
  Tooltip,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  Upload, 
  Plus, 
  X, 
  Save, 
  Globe, 
  Calendar, 
  ChevronDown, 
  ChevronUp, 
  Check,
  Link as LinkIcon,
  Edit,
  Copy,
  CheckCircle2,
  Trash2,
  Image,
  MapPin,
  Home,
  Video,
  AlertTriangle,
  Info
} from 'lucide-react';
import axios from 'axios';
import myAGTService from '../../../services/myAGTService';
import AvailabilityCalendar from './AvailabilityCalendar';
import StoriesCircle from '../../public/StoriesCircle';
import StoryViewer from '../../public/StoryViewer';
import ProfessionalServices from './ProfessionalServices';

// Transición para los modales estilo Apple
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Definir color principal (verde Apple)
const appleGreen = '#34c759';

// Componentes estilizados
const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
  }
}));

const AppleTextField = styled(TextField)(({ theme, size }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#f5f5f7',
    border: 'none',
    '& fieldset': { border: 'none' },
    '&:hover': {
      backgroundColor: '#eaeaec',
    },
    '&.Mui-focused': {
      backgroundColor: '#fff',
      boxShadow: `0 0 0 2px ${appleGreen}40`,
    },
    fontSize: size === 'small' ? '0.875rem' : '1rem',
  },
  '& .MuiInputLabel-root': {
    fontSize: size === 'small' ? '0.875rem' : '1rem',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: appleGreen,
  }
}));

const AppleButton = styled(Button)(({ theme, variant, size }) => ({
  borderRadius: '12px',
  padding: size === 'small' ? theme.spacing(0.8, 1.5) : theme.spacing(1.2),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: size === 'small' ? '0.85rem' : '0.95rem',
  backgroundColor: variant === 'contained' ? appleGreen : 'transparent',
  color: variant === 'contained' ? 'white' : appleGreen,
  border: variant === 'outlined' ? `1px solid ${appleGreen}` : 'none',
  '&:hover': {
    backgroundColor: variant === 'contained' 
      ? theme.palette.success.dark 
      : 'rgba(52, 199, 89, 0.1)',
  },
  boxShadow: 'none',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  color: '#1d1d1f',
  fontSize: '1.1rem',
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  marginBottom: theme.spacing(1),
  color: '#424242',
  fontSize: '0.875rem',
}));

const SpecialtyChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: 'rgba(52, 199, 89, 0.1)',
  color: appleGreen,
  fontWeight: 500,
  border: '1px solid rgba(52, 199, 89, 0.2)',
  borderRadius: '20px',
  '& .MuiChip-deleteIcon': {
    color: appleGreen,
    '&:hover': {
      color: theme.palette.success.dark,
    },
  },
}));

// Componente para el toggle estilo iOS
const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 1,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: appleGreen,
        opacity: 1,
        border: 0,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

// Componente para el modal de subida de imagen de perfil
const ProfileImageUploadModal = ({ open, onClose, imagePreview, onUpload, uploading }) => {
  return (
    <Dialog
      open={open}
      onClose={() => !uploading && onClose()}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, pt: 3 }}>
        Actualizar foto de perfil
      </DialogTitle>
      <DialogContent>
        {imagePreview && (
          <Box sx={{ 
            width: '100%', 
            display: 'flex',
            justifyContent: 'center',
            my: 2
          }}>
            <Avatar
              src={imagePreview}
              alt="Vista previa"
              sx={{
                width: 200,
                height: 200,
                border: '2px solid rgba(0, 0, 0, 0.05)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
              }}
            />
          </Box>
        )}
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          Esta imagen se mostrará en tu perfil público y será visible para todos los usuarios.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <AppleButton
          onClick={onClose}
          variant="outlined"
          disabled={uploading}
        >
          Cancelar
        </AppleButton>
        <AppleButton
          onClick={onUpload}
          variant="contained"
          startIcon={uploading ? null : <Save size={18} />}
          disabled={uploading}
        >
          {uploading ? <CircularProgress size={24} color="inherit" /> : 'Guardar foto'}
        </AppleButton>
      </DialogActions>
    </Dialog>
  );
};

const MyAGTProfile = ({ user }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState({
    name: `${user?.firstName || ''} ${user?.lastName || ''}`,
    slug: '',
    title: '',
    description: '',
    yearsExperience: '',
    specialties: [],
    profileImage: null,
    isPublished: false,
    // Campos para modalidades de atención
    appointmentModes: {
      inPerson: false,
      homeVisit: false,
      online: false
    },
    appointmentLocations: {
      inPersonAddress: '',
      inPersonMapLink: '',
      inPersonInstructions: '',
      onlineMeetingLink: '',
      onlinePlatform: 'zoom' // Por defecto Zoom
    },
    // Servicios profesionales
    professionalServices: []
  });
  const [newSpecialty, setNewSpecialty] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [availability, setAvailability] = useState({
    workDays: [1, 2, 3, 4, 5], // Lunes a viernes por defecto
    workHours: {
      start: '09:00',
      end: '18:00'
    },
    slotDuration: 30, // minutos
    exceptions: [] // fechas bloqueadas o con horarios especiales
  });
  const [isEditMode, setIsEditMode] = useState(true); // Por defecto en modo edición
  
  // Estados para los modales
  const [openUnpublishedModal, setOpenUnpublishedModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openValidationModal, setOpenValidationModal] = useState(false); // Nuevo modal para validaciones
  const [copied, setCopied] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]); // Para almacenar errores de validación

  // Estados para historias
  const [stories, setStories] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);
  const [storyViewerOpen, setStoryViewerOpen] = useState(false);
  const [loadingStories, setLoadingStories] = useState(false);
  const [storyImage, setStoryImage] = useState(null);
  const [storyCaption, setStoryCaption] = useState('');
  const [storyDialogOpen, setStoryDialogOpen] = useState(false);
  const [uploadingStory, setUploadingStory] = useState(false);
  const [deleteStoryDialogOpen, setDeleteStoryDialogOpen] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState(null);
  const [deletingStory, setDeletingStory] = useState(false);

  // Estados para el modal de imagen de perfil
  const [profileImageDialogOpen, setProfileImageDialogOpen] = useState(false);
  const [tempProfileImage, setTempProfileImage] = useState(null);
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);

  // Función para copiar al portapapeles
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Cargar datos del perfil al iniciar
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!user?.userId) return;
  
      setLoading(true);
      try {
        // Obtener el perfil público
        const response = await myAGTService.getPublicProfile();
        console.log("Datos recibidos del backend:", response);
  
        if (response.success && response.data) {
          const backendData = response.data;
  
          // Construir el nombre correctamente desde firstName y lastName
          // en lugar de intentar usar el campo name
          const displayName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Usuario';
  
          setProfileData({
            name: displayName, // Usar siempre firstName + lastName
            slug: backendData.slug || '',
            title: backendData.title || '',
            description: backendData.description || '',
            yearsExperience: backendData.yearsExperience || '',
            specialties: backendData.specialties || [],
            profileImageUrl: backendData.profileImageUrl || null,
            profileImage: null,
            isPublished: backendData.isPublished === true,
            // Inicializar modalidades de atención con datos del backend o valores por defecto
            appointmentModes: backendData.appointmentModes || {
              inPerson: false,
              homeVisit: false,
              online: false
            },
            appointmentLocations: backendData.appointmentLocations || {
              inPersonAddress: '',
              inPersonMapLink: '',
              inPersonInstructions: '',
              onlineMeetingLink: '',
              onlinePlatform: 'zoom'
            },
            // Inicializar servicios profesionales
            professionalServices: backendData.professionalServices || []
          });
  
          // Confiar en lo que dice el backend para el modo de edición
          if (backendData.isPublished === true) {
            setIsEditMode(false);
          } else {
            setIsEditMode(true);
          }
        }
  
        // Cargar disponibilidad
        const availabilityResponse = await myAGTService.getAvailability(user.userId);
        if (availabilityResponse.success && availabilityResponse.data) {
          setAvailability(availabilityResponse.data);
        }
  
        // Cargar historias
        fetchStories();
      } catch (err) {
        console.error('Error al cargar datos del perfil:', err);
        // Si es el primer acceso, no mostrar error
      } finally {
        setLoading(false);
      }
    };
  
    fetchProfileData();
  }, [user]);

  // Añade este efecto para depurar el estado de la imagen
  useEffect(() => {
    if (profileData.profileImageUrl) {
      console.log("URL de imagen actual:", profileData.profileImageUrl);
   
      // Verificar si la imagen es accesible
      const checkImageAccess = async () => {
        try {
          const response = await fetch(profileData.profileImageUrl, { method: 'HEAD' });
          console.log("Verificación de acceso a imagen:", 
            response.ok ? "EXITOSA" : "FALLIDA", 
            "Status:", response.status);
        } catch (error) {
          console.error("Error al verificar acceso a imagen:", error);
        }
      };
   
      checkImageAccess();
    }
  }, [profileData.profileImageUrl]);

  // Generar slug automáticamente basado en el nombre (formato nombreapellido)
  useEffect(() => {
    if (!profileData.slug && profileData.name) {
      // Eliminar prefijos comunes como Dr., Dra., etc.
      let cleanName = profileData.name
        .replace(/^Dr\.\s+/i, '')
        .replace(/^Dra\.\s+/i, '')
        .replace(/^Doctor\s+/i, '')
        .replace(/^Doctora\s+/i, '')
        .trim();
  
      const nameParts = cleanName.split(' ');
      if (nameParts.length >= 2) {
        // Tomar el primer nombre y el último apellido
        const firstName = nameParts[0].toLowerCase();
        const lastName = nameParts[nameParts.length - 1].toLowerCase();
  
        // Crear slug sin guiones ni espacios (estilo username Instagram)
        const generatedSlug = `${firstName}${lastName}`
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "") // Eliminar acentos
          .replace(/[^\w]/g, ''); // Eliminar caracteres especiales
  
        setProfileData(prev => ({
          ...prev,
          slug: generatedSlug
        }));
      }
    }
  }, [profileData.name, profileData.slug]);

  // Manejar cambios en los campos
  const handleChange = (e) => {
    const { name, value } = e.target;
  
    // No permitir cambios en el slug ni en el nombre
    if (name === 'slug' || name === 'name') return;
  
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Toggle para modo edición
  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  // Añadir especialidad
  const handleAddSpecialty = () => {
    if (!newSpecialty.trim()) return;
    
    setProfileData(prev => ({
      ...prev,
      specialties: [...prev.specialties, newSpecialty.trim()]
    }));
    setNewSpecialty('');
  };

  // Eliminar especialidad
  const handleRemoveSpecialty = (specialty) => {
    setProfileData(prev => ({
      ...prev,
      specialties: prev.specialties.filter(item => item !== specialty)
    }));
  };

  // Manejar cambio de imagen
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
   
    console.log("Imagen seleccionada:", file.name, `(${file.size} bytes, ${file.type})`);
   
    // Validar el tamaño máximo (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("La imagen es demasiado grande. El tamaño máximo es 5MB.");
      return;
    }
   
    // Validar el tipo de archivo
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setError("Formato de imagen no válido. Usa JPG, PNG, WEBP o GIF.");
      return;
    }
   
    // Previsualizar imagen
    const reader = new FileReader();
    reader.onloadend = () => {
      console.log("Vista previa de imagen generada");
      setTempProfileImage({
        file,
        preview: reader.result
      });
      setProfileImageDialogOpen(true);
    };
    reader.onerror = () => {
      console.error("Error al generar vista previa de imagen");
      setError("No se pudo procesar la imagen. Intenta con otra.");
    };
    reader.readAsDataURL(file);
  };

  // Función para subir la imagen de perfil
  const handleUploadProfileImage = async () => {
    if (!tempProfileImage?.file) return;
  
    setUploadingProfileImage(true);
    try {
      // 1. Obtener URL prefirmada
      const uploadUrlResponse = await myAGTService.getProfileImageUploadUrl(
        tempProfileImage.file.type, 
        tempProfileImage.file.name
      );
  
      if (!uploadUrlResponse.success) {
        throw new Error('Error al obtener URL para subida de imagen');
      }
  
      const { presignedUrl, publicUrl } = uploadUrlResponse.data;
  
      // 2. Subir archivo directamente a S3
      await axios.put(presignedUrl, tempProfileImage.file, {
        headers: {
          'Content-Type': tempProfileImage.file.type,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Progreso de subida de imagen de perfil: ${percentCompleted}%`);
        }
      });
  
      // 3. Actualizar el estado con la nueva imagen
      setProfileData(prev => ({
        ...prev,
        profileImageUrl: publicUrl,
        profileImage: null
      }));
  
      // 4. Cerrar el diálogo y limpiar la imagen temporal
      setProfileImageDialogOpen(false);
      setTempProfileImage(null);
  
      // 5. Guardar inmediatamente el perfil con la nueva URL de imagen para persistirla
      const savedProfile = await myAGTService.savePublicProfileDirect({
        ...profileData,
        profileImageUrl: publicUrl
      });
  
      if (savedProfile.success) {
        console.log("Perfil con nueva imagen guardado correctamente:", savedProfile.data);
      }
  
      // 6. Mostrar mensaje de éxito
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error al subir imagen de perfil:', error);
      setError('Error al subir la imagen. Inténtalo de nuevo.');
    } finally {
      setUploadingProfileImage(false);
    }
  };

  // Manejar cambios en servicios profesionales
  const handleServicesChange = (updatedServices) => {
    setProfileData(prev => ({
      ...prev,
      professionalServices: updatedServices
    }));
  };

  // Validar campos obligatorios
  const validateRequiredFields = () => {
    const errors = [];
  
    // 1. Validar servicios profesionales
    console.log("Validando servicios profesionales:", profileData.professionalServices);
  
    // Verificar si hay servicios
    if (!profileData.professionalServices || profileData.professionalServices.length === 0) {
      errors.push("Debes configurar al menos un servicio profesional activo.");
    } else {
      // Verificar si hay servicios activos
      const activeServices = profileData.professionalServices.filter(service => service.active || service.isActive);
      console.log("Servicios activos encontrados:", activeServices.length);
  
      if (activeServices.length === 0) {
        errors.push("Debes configurar al menos un servicio profesional activo.");
      }
    }
  
    // 2. Validar modalidades de atención
    const hasAnyMode = profileData.appointmentModes.inPerson || 
                      profileData.appointmentModes.homeVisit || 
                      profileData.appointmentModes.online;
    if (!hasAnyMode) {
      errors.push("Debes seleccionar al menos una modalidad de atención.");
    }
  
    // 3. Validar campos adicionales para modalidades seleccionadas
    if (profileData.appointmentModes.inPerson && !profileData.appointmentLocations.inPersonAddress) {
      errors.push("Debes ingresar la dirección de consulta para atención presencial.");
    }
  
    // 4. Validar disponibilidad (workDays debe tener al menos un día)
    if (availability.workDays.length === 0) {
      errors.push("Debes configurar al menos un día de disponibilidad en tu calendario.");
    }
  
    return errors;
  };

  // Guardar perfil
  const handleSaveProfile = async () => {
    // Si el usuario está intentando publicar, validar campos obligatorios
    if (profileData.isPublished) {
      const validationErrors = validateRequiredFields();
      if (validationErrors.length > 0) {
        setValidationErrors(validationErrors);
        setOpenValidationModal(true);
        return;
      }
    }
    
    // Si el perfil está marcado como no publicado, mostrar modal de confirmación
    if (!profileData.isPublished) {
      setOpenUnpublishedModal(true);
      return;
    }
    
    // Continuar con el guardado normal
    await saveProfileData();
  };
  
  // Función para guardar los datos del perfil
  const saveProfileData = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
  
    try {
      // Asegurarnos de que el nombre está correctamente formateado
      const nameToSave = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Usuario';
  
      // Preparar datos para enviar al backend usando el método DIRECT
      const dataToSave = {
        ...profileData,
        name: nameToSave, // Siempre usar firstName + lastName
        // Asegurarse de que estos campos se incluyen explícitamente
        title: profileData.title || '',
        description: profileData.description || '',
        yearsExperience: profileData.yearsExperience || null,
        specialties: profileData.specialties || [],
        professionalServices: profileData.professionalServices || [],
        appointmentModes: profileData.appointmentModes || {},
        appointmentLocations: profileData.appointmentLocations || {}
      };
  
      // Usar el método DIRECT para guardar (este método maneja mejor todos los campos)
      const response = await myAGTService.savePublicProfileDirect(dataToSave);
  
      // Guardar disponibilidad
      await myAGTService.saveAvailability(user.userId, availability);
  
      setSuccess(true);
  
      // Actualizar datos con la respuesta
      if (response.data) {
        console.log("Respuesta del servidor después de guardar:", response.data);
  
        // Actualizar correctamente todos los campos con los datos del backend
        setProfileData(prev => ({
          ...prev,
          ...response.data,
          // Mantener el nombre formateado correctamente
          name: nameToSave,
          // Mantener el objeto profileImage pero actualizar la URL
          profileImageUrl: response.data.profileImageUrl || prev.profileImageUrl,
          // Limpiar el objeto de imagen temporal ya que se ha subido
          profileImage: null
        }));
  
        // Si se acaba de publicar, cambiar a modo visualización
        if (profileData.isPublished) {
          setIsEditMode(false);
        }
  
        // Mostrar el modal de éxito si el perfil está publicado
        if (profileData.isPublished) {
          setOpenSuccessModal(true);
        }
      }
    } catch (err) {
      console.error('Error al guardar perfil:', err);
      setError(err.response?.data?.message || 'Error al guardar el perfil');
    } finally {
      setSaving(false);
  
      // Mostrar mensaje de éxito por 3 segundos
      if (!error) {
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      }
    }
  };
  
  // Función para confirmar guardado desde el modal
  const handleConfirmSave = async () => {
    setOpenUnpublishedModal(false);
    await saveProfileData();
  };
  
  // Función para cambiar el estado de publicación desde el modal
  const handleTogglePublishFromModal = () => {
    setProfileData(prev => ({
      ...prev,
      isPublished: !prev.isPublished
    }));
  };

  // Manejar cambios en disponibilidad
  const handleAvailabilityChange = (newAvailability) => {
    setAvailability(newAvailability);
  };

  // Funciones para manejar historias
  const fetchStories = async () => {
    if (!user?.userId) return;
  
    setLoadingStories(true);
    try {
      // Usa el endpoint correcto para obtener tus propias historias
      const response = await myAGTService.getStories(user.userId);
      // O alternativamente, usa myAGTService.getMyStories() si tienes ese método
      if (response.success) {
        setStories(response.data || []);
      }
    } catch (error) {
      console.error('Error al cargar historias:', error);
    } finally {
      setLoadingStories(false);
    }
  };

  const handleStoryImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Previsualizar imagen
    const reader = new FileReader();
    reader.onloadend = () => {
      setStoryImage({
        file,
        preview: reader.result
      });
    };
    reader.readAsDataURL(file);
    
    // Abrir diálogo para completar la historia
    setStoryDialogOpen(true);
  };

  const handleUploadStory = async () => {
    if (!storyImage?.file) return;
    
    setUploadingStory(true);
    try {
      await myAGTService.uploadStory(storyImage.file, storyCaption);
      
      // Recargar historias
      fetchStories();
      
      // Cerrar diálogo y limpiar estados
      setStoryDialogOpen(false);
      setStoryImage(null);
      setStoryCaption('');
      
      // Mostrar mensaje de éxito
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error al subir historia:', error);
      setError('Error al subir la historia. Inténtalo de nuevo.');
    } finally {
      setUploadingStory(false);
    }
  };

  const confirmDeleteStory = (story) => {
    setStoryToDelete(story);
    setDeleteStoryDialogOpen(true);
  };

  const handleDeleteStory = async () => {
    if (!storyToDelete?.storyId) return;
    
    setDeletingStory(true);
    try {
      await myAGTService.deleteStory(storyToDelete.storyId);
      
      // Actualizar lista de historias
      setStories(prev => prev.filter(story => story.storyId !== storyToDelete.storyId));
      
      // Si la historia eliminada es la que se está viendo, cerrar el visor
      if (selectedStory?.storyId === storyToDelete.storyId) {
        setStoryViewerOpen(false);
      }

      // Cerrar diálogo
      setDeleteStoryDialogOpen(false);
      setStoryToDelete(null);
    } catch (error) {
      console.error('Error al eliminar historia:', error);
      setError('Error al eliminar la historia. Inténtalo de nuevo.');
    } finally {
      setDeletingStory(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress style={{ color: appleGreen }} />
      </Box>
    );
  }

  return (
    <Box sx={{ py: isMobile ? 1.5 : 2, maxWidth: '1200px', mx: 'auto' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }}>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 600, 
            color: '#1d1d1f',
            fontSize: isSmall ? '1.25rem' : '1.5rem'
          }}
        >
          Mi perfil público
        </Typography>
        
        {profileData.isPublished && (
          <AppleButton
            variant={isEditMode ? "outlined" : "contained"}
            size={isSmall ? "small" : "medium"}
            startIcon={isEditMode ? <X size={isSmall ? 16 : 18} /> : <Edit size={isSmall ? 16 : 18} />}
            onClick={toggleEditMode}
          >
            {isEditMode ? 'Cancelar edición' : 'Editar perfil'}
          </AppleButton>
        )}
      </Box>
      
      {/* Notificaciones */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="error" 
          sx={{ 
            width: '100%', 
            borderRadius: '10px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={Fade}
      >
        <Alert 
          severity="success" 
          icon={<Check size={20} />}
          sx={{ 
            width: '100%', 
            borderRadius: '10px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            backgroundColor: appleGreen,
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          Perfil guardado correctamente
        </Alert>
      </Snackbar>
      
      {/* Sección de información personal */}
      <StyledPaper sx={{ p: isSmall ? 2 : 3, mb: isSmall ? 2.5 : 4 }}>
        <Grid container spacing={isSmall ? 2 : 3}>
          <Grid item xs={12} sm={4} md={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box 
              sx={{ 
                position: 'relative',
                width: isSmall ? 100 : 120,
                height: isSmall ? 100 : 120,
                mb: 2
              }}
            >
              <Avatar
                src={profileData.profileImage?.preview || profileData.profileImageUrl}
                sx={{ 
                  width: '100%', 
                  height: '100%',
                  border: '2px solid rgba(0, 0, 0, 0.05)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
                }}
                imgProps={{
                  onError: (e) => {
                    console.error("Error al cargar la imagen de perfil:", e);
                    // Intentar cargar de nuevo con un parámetro de caché
                    if (!e.target.src.includes('?nocache=')) {
                      e.target.src = `${profileData.profileImageUrl}?nocache=${new Date().getTime()}`;
                    } else {
                      // Si ya intentamos recargar y sigue fallando, mostrar un placeholder
                      e.target.src = `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100%' height='100%'><rect fill='%23cccccc' width='100%' height='100%'/><text fill='%23666666' font-family='Arial' font-size='24' x='50%' y='50%' text-anchor='middle' dominant-baseline='middle'>${profileData.name?.charAt(0) || 'U'}</text></svg>`;
                    }
                  },
                  style: { objectFit: 'cover' }
                }}
              />
              {isEditMode && (
                <>
                  <input
                    accept="image/*"
                    id="profile-image-input"
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleImageChange}
                  />
                  <label htmlFor="profile-image-input">
                    <IconButton 
                      component="span"
                      sx={{ 
                        position: 'absolute',
                        bottom: -5,
                        right: -5,
                        backgroundColor: appleGreen,
                        color: 'white',
                        width: 32,
                        height: 32,
                        '&:hover': {
                          backgroundColor: theme.palette.success.dark,
                        }
                      }}
                    >
                      <Upload size={16} />
                    </IconButton>
                  </label>
                </>
              )}
            </Box>
            
            {!isSmall && (
              <Typography 
                variant="body2" 
                color="text.secondary"
                align="center"
                sx={{ mt: 1, fontSize: '0.85rem' }}
              >
                Sube una foto profesional para tu perfil público
              </Typography>
            )}
          </Grid>
          
          <Grid item xs={12} sm={8} md={9}>
            <Grid container spacing={isSmall ? 2 : 3}>
              <Grid item xs={12}>
                <SectionSubtitle>Nombre completo</SectionSubtitle>
                <AppleTextField
                  fullWidth
                  placeholder="Nombre y apellidos"
                  name="name"
                  value={profileData.name}
                  variant="outlined"
                  required
                  size={isSmall ? "small" : "medium"}
                  disabled={true} // Siempre deshabilitado, independiente del modo de edición
                  helperText="Este campo no es editable y se obtiene de tu información de cuenta"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <SectionSubtitle>Mi URL</SectionSubtitle>
                <AppleTextField
                  fullWidth
                  placeholder="url-automatica"
                  name="slug"
                  value={profileData.slug}
                  variant="outlined"
                  required
                  helperText="Esta será la URL pública de tu perfil (no editable)"
                  size={isSmall ? "small" : "medium"}
                  InputProps={{
                    startAdornment: (
                      <Box 
                        component="span" 
                        sx={{ 
                          color: 'text.secondary', 
                          mr: 0.5,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <LinkIcon size={16} style={{ marginRight: 4 }} />
                        /p/
                      </Box>
                    ),
                    readOnly: true,
                  }}
                  disabled={true}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <SectionSubtitle>Título profesional</SectionSubtitle>
                <AppleTextField
                  fullWidth
                  placeholder="Ej: Médico Cardiólogo"
                  name="title"
                  value={profileData.title}
                  onChange={handleChange}
                  variant="outlined"
                  size={isSmall ? "small" : "medium"}
                  disabled={!isEditMode}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <SectionSubtitle>Años de experiencia</SectionSubtitle>
                <AppleTextField
                  fullWidth
                  placeholder="Ej: 5"
                  name="yearsExperience"
                  type="number"
                  value={profileData.yearsExperience}
                  onChange={handleChange}
                  variant="outlined"
                  size={isSmall ? "small" : "medium"}
                  InputProps={{ inputProps: { min: 0 } }}
                  disabled={!isEditMode}
                />
              </Grid>
              
              <Grid item xs={12}>
                <SectionSubtitle>Descripción profesional</SectionSubtitle>
                <AppleTextField
                  fullWidth
                  placeholder="Describe tu experiencia, formación y especialización..."
                  name="description"
                  value={profileData.description}
                  onChange={handleChange}
                  variant="outlined"
                  multiline
                  rows={4}
                  size={isSmall ? "small" : "medium"}
                  disabled={!isEditMode}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </StyledPaper>
      
      {/* Sección de especialidades */}
      <StyledPaper sx={{ p: isSmall ? 2 : 3, mb: isSmall ? 2.5 : 4 }}>
        <SectionTitle>Especialidades</SectionTitle>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {profileData.specialties.length > 0 ? (
            profileData.specialties.map((specialty, index) => (
              <SpecialtyChip 
                key={index}
                label={specialty}
                onDelete={isEditMode ? () => handleRemoveSpecialty(specialty) : undefined}
                size={isSmall ? "small" : "medium"}
              />
            ))
          ) : (
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontStyle: 'italic', py: 1 }}
            >
              Añade tus especialidades para que los pacientes puedan encontrarte más fácilmente
            </Typography>
          )}
        </Box>
        
        {isEditMode && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AppleTextField
              size={isSmall ? "small" : "medium"}
              placeholder="Añadir especialidad"
              value={newSpecialty}
              onChange={(e) => setNewSpecialty(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddSpecialty()}
              sx={{ mr: 1 }}
              inputProps={{ maxLength: 50 }}
            />
            <AppleButton 
              variant="contained" 
              onClick={handleAddSpecialty}
              size={isSmall ? "small" : "medium"}
              startIcon={<Plus size={isSmall ? 16 : 18} />}
              disabled={!newSpecialty.trim()}
            >
              Añadir
            </AppleButton>
          </Box>
        )}
      </StyledPaper>
      
      {/* Sección de servicios profesionales */}
      <StyledPaper sx={{ 
        p: isSmall ? 2 : 3, 
        mb: isSmall ? 2.5 : 4,
        border: profileData.isPublished && profileData.professionalServices.filter(s => s.active).length === 0 
          ? '2px solid #ff3b30' 
          : '1px solid rgba(0, 0, 0, 0.05)'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <SectionTitle sx={{ mb: 0 }}>Servicios profesionales</SectionTitle>
          {profileData.isPublished && profileData.professionalServices.filter(s => s.active).length === 0 && (
            <Chip 
              icon={<AlertTriangle size={16} />} 
              label="Obligatorio" 
              color="error" 
              size="small"
              sx={{ fontWeight: 500 }}
            />
          )}
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configura los servicios que ofreces a tus pacientes y sus precios. Los servicios inactivos no se mostrarán en tu perfil público.
        </Typography>
        
        <ProfessionalServices 
          services={profileData.professionalServices}
          onChange={handleServicesChange}
          isEditMode={isEditMode}
        />
      </StyledPaper>
      
      {/* Sección de modalidades de atención */}
      <StyledPaper sx={{ 
        p: isSmall ? 2 : 3, 
        mb: isSmall ? 2.5 : 4,
        border: profileData.isPublished && 
                !(profileData.appointmentModes.inPerson || 
                  profileData.appointmentModes.homeVisit || 
                  profileData.appointmentModes.online)
          ? '2px solid #ff3b30' 
          : '1px solid rgba(0, 0, 0, 0.05)'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <SectionTitle sx={{ mb: 0 }}>Modalidades de atención</SectionTitle>
          {profileData.isPublished && 
           !(profileData.appointmentModes.inPerson || 
             profileData.appointmentModes.homeVisit || 
             profileData.appointmentModes.online) && (
            <Chip 
              icon={<AlertTriangle size={16} />} 
              label="Obligatorio" 
              color="error" 
              size="small"
              sx={{ fontWeight: 500 }}
            />
          )}
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Selecciona las modalidades de atención que ofreces a tus pacientes.
        </Typography>
        
        <Grid container spacing={3}>
          {/* Atención presencial */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <IOSSwitch
                  checked={profileData.appointmentModes.inPerson}
                  onChange={(e) => setProfileData(prev => ({
                    ...prev,
                    appointmentModes: {
                      ...prev.appointmentModes,
                      inPerson: e.target.checked
                    }
                  }))}
                  disabled={!isEditMode}
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Atención presencial
                </Typography>
              }
            />
            
            <Collapse in={profileData.appointmentModes.inPerson && isEditMode}>
              <Box sx={{ pl: 4, mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <AppleTextField
                      fullWidth
                      label="Dirección de consulta"
                      placeholder="Ej: Av. Providencia 1234, Providencia, Santiago"
                      name="inPersonAddress"
                      value={profileData.appointmentLocations.inPersonAddress}
                      onChange={(e) => setProfileData(prev => ({
                        ...prev,
                        appointmentLocations: {
                          ...prev.appointmentLocations,
                          inPersonAddress: e.target.value
                        }
                      }))}
                      size={isSmall ? "small" : "medium"}
                      disabled={!isEditMode}
                      required={profileData.appointmentModes.inPerson}
                      error={profileData.appointmentModes.inPerson && profileData.isPublished && !profileData.appointmentLocations.inPersonAddress}
                      helperText={profileData.appointmentModes.inPerson && profileData.isPublished && !profileData.appointmentLocations.inPersonAddress 
                        ? "Este campo es obligatorio para la atención presencial" 
                        : ""}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <AppleTextField
                      fullWidth
                      label="Link de Google Maps o Apple Maps"
                      placeholder="https://maps.google.com/..."
                      name="inPersonMapLink"
                      value={profileData.appointmentLocations.inPersonMapLink}
                      onChange={(e) => setProfileData(prev => ({
                        ...prev,
                        appointmentLocations: {
                          ...prev.appointmentLocations,
                          inPersonMapLink: e.target.value
                        }
                      }))}
                      size={isSmall ? "small" : "medium"}
                      disabled={!isEditMode}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MapPin size={16} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <AppleTextField
                      fullWidth
                      label="Instrucciones adicionales"
                      placeholder="Ej: Piso 3, oficina 412. Tocar timbre."
                      name="inPersonInstructions"
                      value={profileData.appointmentLocations.inPersonInstructions}
                      onChange={(e) => setProfileData(prev => ({
                        ...prev,
                        appointmentLocations: {
                          ...prev.appointmentLocations,
                          inPersonInstructions: e.target.value
                        }
                      }))}
                      multiline
                      rows={2}
                      size={isSmall ? "small" : "medium"}
                      disabled={!isEditMode}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
            
            {/* Mostrar información cuando no está en modo edición */}
            {profileData.appointmentModes.inPerson && !isEditMode && (
              <Box sx={{ pl: 4, mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  {profileData.appointmentLocations.inPersonAddress}
                </Typography>
                {profileData.appointmentLocations.inPersonMapLink && (
                  <Link 
                    href={profileData.appointmentLocations.inPersonMapLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      color: appleGreen, 
                      mb: 1,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    <MapPin size={16} style={{ marginRight: 8 }} />
                    Ver ubicación en mapa
                  </Link>
                )}
                {profileData.appointmentLocations.inPersonInstructions && (
                  <Typography variant="body2" color="text.secondary">
                    {profileData.appointmentLocations.inPersonInstructions}
                  </Typography>
                )}
              </Box>
            )}
          </Grid>
          
          {/* Atención a domicilio */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <IOSSwitch
                  checked={profileData.appointmentModes.homeVisit}
                  onChange={(e) => setProfileData(prev => ({
                    ...prev,
                    appointmentModes: {
                      ...prev.appointmentModes,
                      homeVisit: e.target.checked
                    }
                  }))}
                  disabled={!isEditMode}
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Atención a domicilio
                </Typography>
              }
            />
            
            <Collapse in={profileData.appointmentModes.homeVisit && isEditMode}>
              <Box sx={{ pl: 4, mt: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Para esta modalidad, el paciente proporcionará su dirección al agendar la cita.
                </Alert>
              </Box>
            </Collapse>
          </Grid>
          
          {/* Atención online */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <IOSSwitch
                  checked={profileData.appointmentModes.online}
                  onChange={(e) => setProfileData(prev => ({
                    ...prev,
                    appointmentModes: {
                      ...prev.appointmentModes,
                      online: e.target.checked
                    }
                  }))}
                  disabled={!isEditMode}
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Atención online
                </Typography>
              }
            />
            
            <Collapse in={profileData.appointmentModes.online && isEditMode}>
              <Box sx={{ pl: 4, mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size={isSmall ? "small" : "medium"}>
                      <InputLabel>Plataforma preferida</InputLabel>
                      <Select
                        value={profileData.appointmentLocations.onlinePlatform}
                        onChange={(e) => setProfileData(prev => ({
                          ...prev,
                          appointmentLocations: {
                            ...prev.appointmentLocations,
                            onlinePlatform: e.target.value
                          }
                        }))}
                        label="Plataforma preferida"
                        disabled={!isEditMode}
                      >
                        <MenuItem value="zoom">Zoom</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <AppleTextField
                      fullWidth
                      label="Link de reunión (opcional)"
                      placeholder="https://zoom.us/j/..."
                      name="onlineMeetingLink"
                      value={profileData.appointmentLocations.onlineMeetingLink}
                      onChange={(e) => setProfileData(prev => ({
                        ...prev,
                        appointmentLocations: {
                          ...prev.appointmentLocations,
                          onlineMeetingLink: e.target.value
                        }
                      }))}
                      size={isSmall ? "small" : "medium"}
                      disabled={!isEditMode}
                      helperText="Si dejas este campo vacío, se generará un link automático para cada cita"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
            
            {/* Mostrar información cuando no está en modo edición */}
            {profileData.appointmentModes.online && !isEditMode && (
              <Box sx={{ pl: 4, mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  Plataforma: {
                    profileData.appointmentLocations.onlinePlatform === 'zoom' ? 'Zoom' :
                    profileData.appointmentLocations.onlinePlatform === 'meet' ? 'Google Meet' :
                    profileData.appointmentLocations.onlinePlatform === 'teams' ? 'Microsoft Teams' :
                    'Plataforma personalizada'
                  }
                </Typography>
                {profileData.appointmentLocations.onlineMeetingLink && (
                  <Typography variant="body2" color="text.secondary">
                    Link personalizado configurado
                  </Typography>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </StyledPaper>
      
      {/* Sección de historias */}
      <StyledPaper sx={{ p: isSmall ? 2 : 3, mb: isSmall ? 2.5 : 4 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3
        }}>
          <SectionTitle sx={{ mb: 0 }}>Mis historias</SectionTitle>
          
          <Box>
            <input
              accept="image/*"
              id="story-image-input"
              type="file"
              style={{ display: 'none' }}
              onChange={handleStoryImageChange}
            />
            <label htmlFor="story-image-input">
              <AppleButton
                component="span"
                variant="contained"
                startIcon={<Plus size={isSmall ? 16 : 18} />}
                size={isSmall ? "small" : "medium"}
              >
                Nueva historia
              </AppleButton>
            </label>
          </Box>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Las historias son visibles para tus pacientes durante 24 horas y aparecen en tu perfil público.
        </Typography>
        
        <Box sx={{ 
          display: 'flex',
          overflowX: 'auto',
          pb: 1,
          '&::-webkit-scrollbar': {
            height: 6
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: 3
          }
        }}>
          {loadingStories ? (
            // Mostrar skeletons mientras carga
            Array(3).fill().map((_, index) => (
              <StoriesCircle key={index} loading={true} />
            ))
          ) : stories.length > 0 ? (
            // Mostrar historias existentes
            stories.map((story) => (
              <Box key={story.storyId} sx={{ position: 'relative' }}>
                <StoriesCircle
                  image={story.imageUrl}
                  name={new Date(story.createdAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                  hasStories={true}
                  isViewed={false}
                  size="medium"
                  onClick={() => {
                    setSelectedStory(story);
                    setStoryViewerOpen(true);
                  }}
                />
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,0,0,0.1)'
                    },
                    width: 28,
                    height: 28
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmDeleteStory(story);
                  }}
                >
                  <Trash2 size={14} color="#ff3b30" />
                </IconButton>
              </Box>
            ))
          ) : (
            // Mensaje cuando no hay historias
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ py: 4, width: '100%', textAlign: 'center', fontStyle: 'italic' }}
            >
              No tienes historias activas. ¡Comparte tu día a día con tus pacientes!
            </Typography>
          )}
        </Box>
      </StyledPaper>
      
      {/* Sección de disponibilidad */}
      <StyledPaper sx={{ 
        p: isSmall ? 2 : 3, 
        mb: isSmall ? 2.5 : 4,
        border: profileData.isPublished && availability.workDays.length === 0
          ? '2px solid #ff3b30' 
          : '1px solid rgba(0, 0, 0, 0.05)'
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2,
          flexDirection: isSmall ? 'column' : 'row'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: isSmall ? 2 : 0 }}>
            <SectionTitle sx={{ mb: isSmall ? 1 : 2, mr: 2 }}>Disponibilidad</SectionTitle>
            {profileData.isPublished && availability.workDays.length === 0 && (
              <Chip 
                icon={<AlertTriangle size={16} />} 
                label="Obligatorio" 
                color="error" 
                size="small"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Box>
          {isEditMode && (
            <AppleButton 
              variant={showCalendar ? "outlined" : "contained"}
              startIcon={<Calendar size={isSmall ? 16 : 18} />}
              endIcon={showCalendar ? <ChevronUp size={isSmall ? 14 : 16} /> : <ChevronDown size={isSmall ? 14 : 16} />}
              onClick={() => setShowCalendar(!showCalendar)}
              size={isSmall ? "small" : "medium"}
            >
              {showCalendar ? 'Ocultar calendario' : 'Configurar horarios'}
            </AppleButton>
          )}
        </Box>
        
        <Collapse in={showCalendar && isEditMode} timeout={300}>
          <Box sx={{ 
            p: isSmall ? 1.5 : 2, 
            backgroundColor: '#f5f5f7', 
            borderRadius: '12px',
            mb: 2
          }}>
            <AvailabilityCalendar 
              availability={availability}
              onChange={handleAvailabilityChange}
              isMobile={isMobile}
            />
          </Box>
        </Collapse>
        
        {(!showCalendar || !isEditMode) && (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              backgroundColor: availability.workDays.length === 0 ? 'rgba(255, 59, 48, 0.08)' : 'rgba(52, 199, 89, 0.08)', 
              p: 2, 
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {availability.workDays.length === 0 ? (
              <>
                <AlertTriangle size={16} style={{ marginRight: 8, color: '#ff3b30' }} />
                Es necesario configurar tu disponibilidad para que los pacientes puedan agendar citas.
              </>
            ) : (
              <>
                <Calendar size={16} style={{ marginRight: 8, color: appleGreen }} />
                {isEditMode ? 
                  'Configura tus horarios de atención para que tus pacientes puedan agendar citas contigo.' :
                  'Horarios de atención configurados. Edita el perfil para modificarlos.'
                }
              </>
            )}
          </Typography>
        )}
      </StyledPaper>
      
      {/* Sección de publicación */}
      <StyledPaper sx={{ p: isSmall ? 2 : 3, mb: isSmall ? 2.5 : 4 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexDirection: isSmall ? 'column' : 'row'
        }}>
          <Box sx={{ width: isSmall ? '100%' : 'auto' }}>
            <SectionTitle sx={{ mb: 1 }}>Publicar perfil</SectionTitle>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Al activar esta opción, tu perfil será visible públicamente y los pacientes podrán agendar citas contigo.
            </Typography>
            
            <Box sx={{ 
              mt: 1, 
              p: 2, 
              backgroundColor: '#f5f5f7', 
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Globe size={16} style={{ marginRight: 10, color: appleGreen }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  URL pública:
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: appleGreen,
                    wordBreak: 'break-all'
                  }}
                >
                  {window.location.origin}/p/{profileData.slug}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {isEditMode && (
            <FormControlLabel
              control={
                <IOSSwitch
                  checked={profileData.isPublished}
                  onChange={(e) => setProfileData(prev => ({ ...prev, isPublished: e.target.checked }))}
                />
              }
              label={
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500,
                    color: profileData.isPublished ? appleGreen : 'text.secondary'
                  }}
                >
                  {profileData.isPublished ? "Publicado" : "No publicado"}
                </Typography>
              }
              sx={{ mt: isSmall ? 2 : 0 }}
            />
          )}
          
          {!isEditMode && (
            <Chip 
              label={profileData.isPublished ? "Publicado" : "No publicado"}
              color={profileData.isPublished ? "success" : "default"}
              sx={{ 
                fontWeight: 500,
                bgcolor: profileData.isPublished ? 'rgba(52, 199, 89, 0.1)' : undefined,
                color: profileData.isPublished ? appleGreen : undefined,
                border: profileData.isPublished ? `1px solid ${appleGreen}20` : undefined,
              }}
            />
          )}
        </Box>
        
        {/* Mensaje de información sobre campos obligatorios */}
        {isEditMode && profileData.isPublished && (
          <Alert 
            severity="info" 
            icon={<Info size={20} />}
            sx={{ mt: 2, borderRadius: '10px' }}
          >
            Para publicar tu perfil, debes configurar al menos un servicio profesional, una modalidad de atención y tu disponibilidad.
          </Alert>
        )}
      </StyledPaper>
      
      {/* Botón de guardar (solo visible en modo edición) */}
      {isEditMode && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          position: 'sticky',
          bottom: isSmall ? 16 : 32,
          zIndex: 10,
          mt: 2
        }}>
          <AppleButton
            variant="contained"
            size={isSmall ? "small" : "medium"}
            startIcon={saving ? null : <Save size={isSmall ? 16 : 18} />}
            onClick={handleSaveProfile}
            disabled={saving}
            sx={{ 
              minWidth: isSmall ? 120 : 150,
              boxShadow: '0 4px 12px rgba(52, 199, 89, 0.3)!important',
              py: isSmall ? 1 : 1.5
            }}
          >
            {saving ? <CircularProgress size={isSmall ? 20 : 24} color="inherit" /> : 'Guardar perfil'}
          </AppleButton>
        </Box>
      )}
      
      {/* Modal para perfil no publicado */}
      <Dialog
        open={openUnpublishedModal}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setOpenUnpublishedModal(false)}
        aria-describedby="unpublished-profile-dialog"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            maxWidth: 500
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 600, 
          pt: 3,
          pb: 1,
          textAlign: 'center'
        }}>
          Tu perfil está desactivado
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="unpublished-profile-dialog" sx={{ 
            textAlign: 'center',
            mb: 3,
            color: '#424242'
          }}>
            Tu perfil está marcado como no visible para los usuarios. Esto significa que nadie podrá ver tu perfil ni agendar citas contigo.
          </DialogContentText>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            backgroundColor: '#f5f5f7',
            p: 2,
            borderRadius: '12px',
            mb: 2
          }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              ¿Quieres hacer visible tu perfil ahora?
            </Typography>
            
            <FormControlLabel
              control={
                <IOSSwitch
                  checked={profileData.isPublished}
                  onChange={handleTogglePublishFromModal}
                />
              }
              label={
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500,
                    color: profileData.isPublished ? appleGreen : 'text.secondary'
                  }}
                >
                  {profileData.isPublished ? "Visible para todos" : "No visible"}
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 1 }}>
          <AppleButton 
            onClick={() => setOpenUnpublishedModal(false)} 
            variant="outlined"
            sx={{ minWidth: 120 }}
          >
            Cancelar
          </AppleButton>
          <AppleButton 
            onClick={handleConfirmSave} 
            variant="contained"
            sx={{ minWidth: 120 }}
          >
            Continuar
          </AppleButton>
        </DialogActions>
      </Dialog>
      
      {/* Modal de éxito con link */}
      <Dialog
        open={openSuccessModal}
        TransitionComponent={Transition}
        onClose={() => setOpenSuccessModal(false)}
        aria-describedby="success-profile-dialog"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            maxWidth: 500
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 600, 
          pt: 3,
          pb: 1,
          textAlign: 'center',
          color: appleGreen
        }}>
          <CheckCircle2 size={40} style={{ marginBottom: 10 }} />
          <Box component="span" display="block">¡Genial!</Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="success-profile-dialog" sx={{ 
            textAlign: 'center',
            mb: 3,
            color: '#424242'
          }}>
            Tu perfil ha sido guardado y publicado correctamente. Ahora las personas podrán agendar citas contigo a través del siguiente enlace:
          </DialogContentText>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            backgroundColor: '#f5f5f7',
            p: 2,
            borderRadius: '12px',
            mb: 2
          }}>
            <AppleTextField
              fullWidth
              value={`${window.location.origin}/p/${profileData.slug}`}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? "¡Copiado!" : "Copiar enlace"} arrow>
                      <IconButton 
                        onClick={() => copyToClipboard(`${window.location.origin}/p/${profileData.slug}`)}
                        edge="end"
                      >
                        {copied ? <CheckCircle2 size={20} color={appleGreen} /> : <Copy size={20} />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
          <AppleButton 
            onClick={() => setOpenSuccessModal(false)} 
            variant="contained"
            sx={{ minWidth: 150 }}
          >
            Entendido
          </AppleButton>
        </DialogActions>
      </Dialog>

      {/* Modal de validación para campos requeridos */}
      <Dialog
        open={openValidationModal}
        TransitionComponent={Transition}
        onClose={() => setOpenValidationModal(false)}
        aria-describedby="validation-dialog"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            maxWidth: 500
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 600, 
          pt: 3,
          pb: 1,
          textAlign: 'center',
          color: '#ff3b30',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <AlertTriangle size={40} style={{ marginBottom: 10 }} />
          <Box component="span" display="block">Información incompleta</Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="validation-dialog" sx={{ 
            textAlign: 'center',
            mb: 2,
            color: '#424242'
          }}>
            Para publicar tu perfil, necesitas completar la siguiente información:
          </DialogContentText>
          
          <Box sx={{ 
            backgroundColor: '#fff8f8',
            border: '1px solid #ffcdd2',
            borderRadius: '12px',
            p: 2,
            mb: 3
          }}>
            <ul style={{ 
              margin: 0, 
              paddingLeft: '20px',
              color: '#d32f2f'
            }}>
              {validationErrors.map((error, index) => (
                <li key={index} style={{ marginBottom: '8px' }}>{error}</li>
              ))}
            </ul>
          </Box>
          
          <Typography variant="body2" color="text.secondary" align="center">
            Puedes guardar tu perfil como borrador (no publicado) y completar esta información más tarde.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
          <AppleButton 
            onClick={() => {
              // Guardar como borrador (no publicado)
              setProfileData(prev => ({ ...prev, isPublished: false }));
              setOpenValidationModal(false);
              saveProfileData();
            }} 
            variant="outlined"
            sx={{ minWidth: 200 }}
          >
            Guardar como borrador
          </AppleButton>
          <AppleButton 
            onClick={() => setOpenValidationModal(false)} 
            variant="contained"
            sx={{ minWidth: 200 }}
          >
            Completar información
          </AppleButton>
        </DialogActions>
      </Dialog>

      {/* Diálogo para subir historias */}
      <Dialog
        open={storyDialogOpen}
        onClose={() => !uploadingStory && setStoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pt: 3 }}>
          Nueva historia
        </DialogTitle>
        <DialogContent>
          {storyImage && (
            <Box sx={{ 
              width: '100%', 
              display: 'flex',
              justifyContent: 'center',
              my: 2
            }}>
              <img
                src={storyImage.preview}
                alt="Vista previa"
                style={{
                  maxWidth: '100%',
                  maxHeight: '300px',
                  borderRadius: '8px',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
          
          <AppleTextField
            fullWidth
            label="Añade una descripción (opcional)"
            value={storyCaption}
            onChange={(e) => setStoryCaption(e.target.value)}
            multiline
            rows={2}
            sx={{ mt: 2 }}
            inputProps={{ maxLength: 150 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Esta historia estará visible durante 24 horas en tu perfil público.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <AppleButton
            onClick={() => setStoryDialogOpen(false)}
            variant="outlined"
            disabled={uploadingStory}
          >
            Cancelar
          </AppleButton>
          <AppleButton
            onClick={handleUploadStory}
            variant="contained"
            startIcon={uploadingStory ? null : <Upload size={18} />}
            disabled={!storyImage || uploadingStory}
          >
            {uploadingStory ? <CircularProgress size={24} color="inherit" /> : 'Publicar'}
          </AppleButton>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para eliminar historia */}
      <Dialog
        open={deleteStoryDialogOpen}
        onClose={() => !deletingStory && setDeleteStoryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pt: 3 }}>
          Eliminar historia
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que quieres eliminar esta historia? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <AppleButton
            onClick={() => setDeleteStoryDialogOpen(false)}
            variant="outlined"
            disabled={deletingStory}
          >
            Cancelar
          </AppleButton>
          <AppleButton
            onClick={handleDeleteStory}
            variant="contained"
            color="error"
            startIcon={deletingStory ? null : <Trash2 size={18} />}
            disabled={deletingStory}
            sx={{ 
              backgroundColor: '#ff3b30',
              '&:hover': {
                backgroundColor: '#d63030',
              }
            }}
          >
            {deletingStory ? <CircularProgress size={24} color="inherit" /> : 'Eliminar'}
          </AppleButton>
        </DialogActions>
      </Dialog>

      {/* Modal para subir imagen de perfil */}
      <ProfileImageUploadModal
        open={profileImageDialogOpen}
        onClose={() => {
          setProfileImageDialogOpen(false);
          setTempProfileImage(null);
        }}
        imagePreview={tempProfileImage?.preview}
        onUpload={handleUploadProfileImage}
        uploading={uploadingProfileImage}
      />

      {/* Visor de historias */}
      {stories.length > 0 && (
        <StoryViewer
          stories={stories}
          open={storyViewerOpen}
          onClose={() => setStoryViewerOpen(false)}
          professionalName={profileData.name}
          professionalImage={profileData.profileImageUrl || profileData.profileImage?.preview}
        />
      )}
    </Box>
  );
};

export default MyAGTProfile;